import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { 
  Activity, 
  Globe, 
  Key, 
  Webhook, 
  Database,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Settings,
  Zap,
  BarChart3,
  Eye,
  Play,
  Pause,
  RotateCcw,
  ExternalLink,
  Shield,
  Server,
  Network,
  XCircle
} from 'lucide-react';
import { useDataContext } from '../../contexts/DataContext';
import Chart from '../../components/ui/Chart';

const IntegrationDashboard = () => {
  const navigate = useNavigate();
  const { getMockData } = useDataContext();
  const [integrationData, setIntegrationData] = useState(null);
  const [webhookActivity, setWebhookActivity] = useState([]);
  const [apiMetrics, setApiMetrics] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showLogs, setShowLogs] = useState(null);
  const [showMetricModal, setShowMetricModal] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);

  useEffect(() => {
    loadIntegrationData();
  }, []);

  const loadIntegrationData = async () => {
    setIsRefreshing(true);
    try {
      const data = getMockData('integrationData');
      setIntegrationData(data);
      setWebhookActivity(data.webhookActivity || []);
      setApiMetrics(data.apiMetrics || {});
    } catch (error) {
      console.error('Failed to load integration data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadIntegrationData();
  };

  const handleIntegrationAction = (integrationId, action) => {
    switch (action) {
      case 'Test Connection':
        alert(`Testing connection for integration ${integrationId}...\n\nConnection Status: ✓ Success\nResponse Time: 120ms\nEndpoint: Reachable`);
        break;
      case 'View Logs':
        setShowLogs(integrationId);
        alert(`Viewing logs for integration ${integrationId}:\n\n[2025-01-15 10:30:00] INFO: Sync completed successfully\n[2025-01-15 10:15:00] INFO: Connection established\n[2025-01-15 10:00:00] INFO: Started sync process`);
        break;
      case 'Configure':
        navigate('/integration/settings');
        break;
      default:
        alert(`Integration action: ${action} for integration ${integrationId}`);
    }
  };

  const handleAddIntegration = () => {
    navigate('/integration/external-systems');
  };

  const handleConfigureAPIs = () => {
    navigate('/integration/api-keys');
  };

  const handleViewAnalytics = () => {
    navigate('/integration/analytics');
  };

  const handleViewAllWebhookActivity = () => {
    navigate('/integration/webhooks');
  };

  const handleMetricClick = (metric) => {
    setSelectedMetric(metric);
    setShowMetricModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'gray';
      case 'error': return 'red';
      case 'warning': return 'yellow';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'inactive': return Clock;
      case 'error': return AlertTriangle;
      case 'warning': return AlertTriangle;
      default: return Activity;
    }
  };

  const integrationMetrics = [
    {
      title: 'Active Integrations',
      value: integrationData?.metrics?.activeIntegrations || 6,
      icon: Globe,
      bgColor: '#006600',
      textColor: 'white',
      trend: '+3',
      trendDirection: 'up',
      details: {
        total: 8,
        active: 6,
        inactive: 1,
        error: 1,
        description: 'Total active system integrations running smoothly'
      }
    },
    {
      title: 'API Calls Today',
      value: apiMetrics?.callsToday || 45230,
      icon: Key,
      bgColor: '#000000',
      textColor: 'white',
      trend: '+15%',
      trendDirection: 'up',
      details: {
        successful: 44120,
        failed: 1110,
        avgResponseTime: '120ms',
        description: 'Total API calls processed in the last 24 hours'
      }
    },
    {
      title: 'Webhook Deliveries',
      value: apiMetrics?.webhookDeliveries || 2139,
      icon: Webhook,
      bgColor: '#006600',
      textColor: 'white',
      trend: '+8%',
      trendDirection: 'up',
      details: {
        successful: 2010,
        failed: 89,
        pending: 40,
        description: 'Webhook events delivered to external systems'
      }
    },
    {
      title: 'Error Rate',
      value: `${apiMetrics?.errorRate || 2.5}%`,
      icon: AlertTriangle,
      bgColor: '#BB0000',
      textColor: 'white',
      trend: '-2%',
      trendDirection: 'down',
      details: {
        totalErrors: 1199,
        critical: 23,
        warning: 176,
        description: 'Overall error rate across all integrations'
      }
    }
  ];

  const integrations = [
    {
      id: 1,
      name: 'Salesforce CRM',
      type: 'CRM',
      status: 'active',
      lastSync: '2025-01-15T10:30:00Z',
      syncFrequency: '15 minutes',
      recordsSynced: 1250,
      errors: 0,
      endpoint: 'https://api.salesforce.com/v1'
    },
    {
      id: 2,
      name: 'Slack Notifications',
      type: 'Communication',
      status: 'active',
      lastSync: '2025-01-15T10:25:00Z',
      syncFrequency: 'Real-time',
      recordsSynced: 890,
      errors: 2,
      endpoint: 'https://hooks.slack.com/services/...'
    },
    {
      id: 3,
      name: 'Jira Project Management',
      type: 'Project Management',
      status: 'warning',
      lastSync: '2025-01-15T09:45:00Z',
      syncFrequency: '30 minutes',
      recordsSynced: 567,
      errors: 5,
      endpoint: 'https://company.atlassian.net/api'
    },
    {
      id: 4,
      name: 'Zendesk Support',
      type: 'Support',
      status: 'active',
      lastSync: '2025-01-15T10:28:00Z',
      syncFrequency: '10 minutes',
      recordsSynced: 2100,
      errors: 0,
      endpoint: 'https://company.zendesk.com/api'
    },
    {
      id: 5,
      name: 'HubSpot Marketing',
      type: 'Marketing',
      status: 'inactive',
      lastSync: '2025-01-14T16:20:00Z',
      syncFrequency: '1 hour',
      recordsSynced: 445,
      errors: 12,
      endpoint: 'https://api.hubapi.com/v1'
    },
    {
      id: 6,
      name: 'Microsoft Teams',
      type: 'Communication',
      status: 'active',
      lastSync: '2025-01-15T10:29:00Z',
      syncFrequency: 'Real-time',
      recordsSynced: 678,
      errors: 1,
      endpoint: 'https://graph.microsoft.com/v1.0'
    }
  ];

  const apiUsageData = [
    { name: '00:00', calls: 1250, errors: 15 },
    { name: '02:00', calls: 980, errors: 12 },
    { name: '04:00', calls: 650, errors: 8 },
    { name: '06:00', calls: 1850, errors: 22 },
    { name: '08:00', calls: 3450, errors: 45 },
    { name: '10:00', calls: 4680, errors: 58 },
    { name: '12:00', calls: 5200, errors: 68 },
    { name: '14:00', calls: 4920, errors: 62 },
    { name: '16:00', calls: 4150, errors: 51 },
    { name: '18:00', calls: 3280, errors: 38 },
    { name: '20:00', calls: 2450, errors: 28 },
    { name: '22:00', calls: 1680, errors: 18 }
  ];

  const webhookStatusData = [
    { name: 'Delivered', value: apiMetrics?.webhookSuccess || 2010, color: '#006600' },
    { name: 'Failed', value: apiMetrics?.webhookFailed || 89, color: '#BB0000' },
    { name: 'In Queue', value: apiMetrics?.webhookPending || 40, color: '#000000' }
  ];

  return (
    <div className="integration-dashboard p-4 bg-background min-h-screen">
      {/* Header */}
      <div className="mb-5 bg-card rounded-2xl shadow-sm p-5 border border-border">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Integration Dashboard</h1>
            <p className="text-muted-foreground mt-1">Monitor all integration statuses and activities</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              onClick={handleRefresh} 
              disabled={isRefreshing}
              className="px-6 py-3 rounded-lg font-medium shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
              style={{
                background: isRefreshing 
                  ? 'linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)' 
                  : 'linear-gradient(135deg, #006600 0%, #006600 100%)',
                color: 'white',
                border: 'none'
              }}
            >
              {isRefreshing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Data
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {integrationMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <Card 
              key={index} 
              className="hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-105"
              onClick={() => handleMetricClick(metric)}
              style={{ 
                background: `linear-gradient(135deg, ${metric.bgColor} 0%, ${metric.bgColor}dd 100%)`,
                border: 'none'
              }}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium" style={{ color: metric.textColor }}>
                  {metric.title}
                </CardTitle>
                <Icon className="h-5 w-5" style={{ color: metric.textColor }} />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" style={{ color: metric.textColor }}>
                  {metric.value.toLocaleString ? metric.value.toLocaleString() : metric.value}
                </div>
                <div className="flex items-center text-sm mt-2" style={{ color: metric.textColor, opacity: 0.9 }}>
                  {metric.trendDirection === 'up' ? (
                    <TrendingUp className="h-4 w-4 mr-1" />
                  ) : (
                    <TrendingDown className="h-4 w-4 mr-1" />
                  )}
                  <span>{metric.trend} from yesterday</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* API Usage Chart */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <BarChart3 className="h-5 w-5 mr-2" style={{ color: '#006600' }} />
              API Usage Trends (24h)
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Real-time API call volume and error tracking</p>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: '#006600' }}></div>
                  <span className="text-sm text-muted-foreground">Total Calls</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: '#BB0000' }}></div>
                  <span className="text-sm text-muted-foreground">Errors</span>
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                Peak: 5,200 calls at 12:00 PM
              </div>
            </div>
            <div className="relative">
              <Chart 
                type="line" 
                data={apiUsageData} 
                height={300}
                colors={['#006600', '#BB0000']}
                dataKeys={[
                  { dataKey: 'calls', name: 'Total Calls' },
                  { dataKey: 'errors', name: 'Errors' }
                ]}
                options={{
                  legend: false
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Webhook Status Chart */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <Webhook className="h-5 w-5 mr-2" style={{ color: '#006600' }} />
              Webhook Delivery Status
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Distribution of webhook delivery outcomes</p>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="grid grid-cols-3 gap-4 mb-4">
                {webhookStatusData.map((item, index) => (
                  <div key={index} className="flex items-center justify-center p-3 bg-[#F8FCFB] rounded-lg hover:bg-[#E0F8F3] transition-colors">
                    <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: item.color }}></div>
                    <div>
                      <div className="text-xs text-muted-foreground font-medium">{item.name}</div>
                      <div className="text-lg font-bold" style={{ color: item.color }}>{item.value.toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <Chart 
              type="pie" 
              data={webhookStatusData} 
              height={260}
              colors={['#006600', '#BB0000', '#000000']}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      padding: 15,
                      font: {
                        size: 12
                      },
                      usePointStyle: true,
                      pointStyle: 'circle'
                    }
                  },
                  tooltip: {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    padding: 12,
                    titleColor: '#fff',
                    bodyColor: '#fff',
                    callbacks: {
                      label: function(context) {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${label}: ${value.toLocaleString()} (${percentage}%)`;
                      }
                    }
                  }
                }
              }}
            />
          </CardContent>
        </Card>
      </div>

      {/* Integrations Grid */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Globe className="h-5 w-5 mr-2" />
            Active Integrations ({integrations.filter(i => i.status === 'active').length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {integrations.map((integration) => {
              const StatusIcon = getStatusIcon(integration.status);
              return (
                <div key={integration.id} className="p-4 border border-border rounded-xl hover:bg-[#F8FCFB] transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-[#E8FCF7] rounded-full flex items-center justify-center">
                        <span className="text-[#006600] font-semibold text-sm">
                          {integration.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{integration.name}</h3>
                        <p className="text-sm text-muted-foreground">{integration.type}</p>
                      </div>
                    </div>
                    <Badge variant={integration.status === 'active' ? 'default' : 'secondary'}>
                      {integration.status}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Last Sync:</span>
                      <span>{new Date(integration.lastSync).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Frequency:</span>
                      <span>{integration.syncFrequency}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Records Synced:</span>
                      <span>{integration.recordsSynced.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Errors:</span>
                      <span className={integration.errors > 0 ? 'text-[#BB0000]' : 'text-[#006600]'}>
                        {integration.errors}
                      </span>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleIntegrationAction(integration.id, 'Test Connection')}
                    >
                      <Play className="h-3 w-3 mr-1" />
                      Test
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleIntegrationAction(integration.id, 'View Logs')}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Logs
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleIntegrationAction(integration.id, 'Configure')}
                    >
                      <Settings className="h-3 w-3 mr-1" />
                      Config
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Webhook Activity Feed */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            Recent Webhook Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {webhookActivity.slice(0, 10).map((activity, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-2 h-2 rounded-full ${
                    activity.status === 'success' ? 'bg-green-500' : 
                    activity.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'
                  }`} />
                  <div>
                    <p className="text-sm font-medium">{activity.event}</p>
                    <p className="text-xs text-gray-500">{activity.integration} • {activity.endpoint}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">{activity.responseTime}ms</p>
                  <p className="text-xs text-gray-500">{new Date(activity.timestamp).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <Button variant="outline" onClick={handleViewAllWebhookActivity}>View All Webhook Activity</Button>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Zap className="h-5 w-5 mr-2" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button onClick={handleAddIntegration} className="h-20 flex flex-col items-center justify-center space-y-2"
            style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}>
              <Globe className="h-6 w-6" />
              <span>Add Integration</span>
            </Button>
            <Button variant="outline" onClick={handleConfigureAPIs} className="h-20 flex flex-col items-center justify-center space-y-2">
              <Settings className="h-6 w-6" />
              <span>Configure APIs</span>
            </Button>
            <Button variant="outline" onClick={handleViewAnalytics} className="h-20 flex flex-col items-center justify-center space-y-2">
              <BarChart3 className="h-6 w-6" />
              <span>View Analytics</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Metric Detail Modal */}
      {selectedMetric && (
        <Modal isOpen={showMetricModal} onClose={() => setShowMetricModal(false)}>
          <div className="max-w-2xl -m-6">
            <div 
              className="px-8 py-6 rounded-t-lg relative"
              style={{ 
                background: `linear-gradient(135deg, ${selectedMetric.bgColor} 0%, ${selectedMetric.bgColor}dd 100%)`,
                color: 'white'
              }}
            >
              <button
                onClick={() => setShowMetricModal(false)}
                className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
                aria-label="Close"
              >
                <XCircle className="h-6 w-6" />
              </button>
              <div className="flex items-center justify-between mb-4 pr-12">
                <h2 className="text-2xl font-bold">{selectedMetric.title}</h2>
                {React.createElement(selectedMetric.icon, { className: 'h-10 w-10' })}
              </div>
              <div className="text-4xl font-bold mb-2">
                {selectedMetric.value.toLocaleString ? selectedMetric.value.toLocaleString() : selectedMetric.value}
              </div>
              <div className="flex items-center text-lg" style={{ opacity: 0.9 }}>
                {selectedMetric.trendDirection === 'up' ? (
                  <TrendingUp className="h-5 w-5 mr-2" />
                ) : (
                  <TrendingDown className="h-5 w-5 mr-2" />
                )}
                <span>{selectedMetric.trend} from yesterday</span>
              </div>
            </div>

            <div className="p-8 space-y-4">
              <p className="text-gray-700 text-lg">{selectedMetric.details.description}</p>
              
              <div className="grid grid-cols-2 gap-4 mt-6">
                {Object.entries(selectedMetric.details).map(([key, value]) => {
                  if (key === 'description') return null;
                  return (
                    <div key={key} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-sm text-gray-600 capitalize mb-1">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                      <div className="text-2xl font-bold" style={{ color: selectedMetric.bgColor }}>
                        {value.toLocaleString ? value.toLocaleString() : value}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end mt-6 space-x-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowMetricModal(false)}
                  className="hover:bg-gray-100 transition-all"
                  style={{
                    borderColor: selectedMetric.bgColor,
                    color: selectedMetric.bgColor
                  }}
                >
                  Close
                </Button>
                <Button 
                  onClick={() => {
                    setShowMetricModal(false);
                    navigate('/integration/analytics');
                  }}
                  className="transition-all hover:scale-105"
                  style={{
                    background: `linear-gradient(135deg, ${selectedMetric.bgColor} 0%, ${selectedMetric.bgColor}dd 100%)`,
                    color: 'white',
                    border: 'none'
                  }}
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  View Detailed Analytics
                </Button>
                <Button 
                  onClick={() => {
                    setShowMetricModal(false);
                    navigate('/integration/settings');
                  }}
                  className="transition-all hover:scale-105"
                  style={{
                    background: 'linear-gradient(135deg, #006600 0%, #006600 100%)',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Configure
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default IntegrationDashboard;
